import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Button } from '../../ui';
import { paidSpan, type PaidSpan } from '../../lib/entitlement';
import { panelVariants, scrimVariants } from '../../lib/motion';
import { sound } from '../../lib/sound';
import { useStrings } from '../../state/useLocale';
import type { InviteProps } from '../contracts';
import type { RedeemResult } from '../../lib/offer';

/**
 * Unlocking the course with an invite code.
 *
 * The code is the way the course is given away — to a reviewer, a student on
 * a scholarship, a room at a conference, somebody whose card will not clear a
 * cross-border charge in dollars. That last one moved when the checkout did
 * and did not go away: the old sentence here said "an Egyptian gateway",
 * because the course used to be sold through one, and every processor leaves
 * somebody outside it. It grants exactly what a payment grants, which
 * is why this dialog is the offer block's equal rather than a footnote
 * beneath it: the person opening it was told a code would work, and the
 * screen should behave as though that were an ordinary thing to be holding
 * rather than an exception being made.
 *
 * ## What it does not do
 *
 * It does not decide anything. `redeem` reports what the Worker said, and the
 * catalogue is then re-read from the one place that knows what this account
 * owns (`usePaid`, via `onRedeemed`). A dialog that concluded "unlocked" from
 * its own successful-looking POST would be a dialog that can be made to
 * conclude it, and the levels are not in the bundle to be revealed anyway —
 * they are thirty files the Worker has and this browser has not.
 *
 * It also does not validate the code before sending it. The shape is checked
 * server-side (worker/invite.ts), and doing it here as well would mean two
 * definitions of what a code looks like, of which the one that matters is not
 * the one the learner would be arguing with. The button is disabled on an
 * empty box and on nothing else.
 *
 * ## The text box
 *
 * `dir="ltr"` in both languages, for the reason Backup's two boxes are:
 * a code is machine text — an opaque run of base32 broken by dashes — and
 * inside a right-to-left page the bidi algorithm reorders it around every
 * character it reads as punctuation, in the box the learner is pasting into.
 * See docs/i18n.md §7.
 *
 * The outcome is written into the panel rather than thrown as a toast, as
 * Backup's restore outcome is and for the same reason: it answers something
 * the person just did, and a refusal is worth being able to read twice.
 */
export function Invite({ open, missions, account, redeem, onRedeemed, onClose }: InviteProps) {
  const s = useStrings().home.invite;
  // The sign-in wait is the same wait the level list names, so it is the same
  // sentence rather than a second one that would drift from it.
  const signingIn = useStrings().home.standing.signingIn;
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<RedeemResult | null>(null);
  const [wasOpen, setWasOpen] = useState(open);

  const panelRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);

  // The paid half is read at the moment a code lands, before the unlock is
  // folded in — afterwards there are no locked stubs left to read it from.
  // Same reasoning as the checkout-return toast in App.
  //
  // The whole span rather than two numbers, because the sentence below has to
  // be able to *not* quote a range: `paidSpan` reports one only while the
  // paid half is one lane's tail, and a wholly paid track has none to report
  // (lib/entitlement.ts).
  const spanAtRedeem = useRef<PaidSpan | null>(null);

  // Worked out during render rather than in an effect, as Backup and Warmup
  // do: by the time effects run the panel has painted, and a stale outcome
  // from the last visit would have been on screen for a frame.
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) {
      setCode('');
      setSaid(null);
      setBusy(false);
      restoreTo.current = document.activeElement as HTMLElement | null;
    }
  }

  // The box takes focus, not the panel. Unlike Backup — which offers two
  // halves and should not choose for you — this dialog has exactly one thing
  // to do, and the person opening it is holding a code ready to paste.
  useEffect(() => {
    if (!open) return;
    fieldRef.current?.focus();
    return () => restoreTo.current?.focus?.();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      // The panel is focusable and deliberately not in that list, so without
      // this the first Shift+Tab escapes behind the scrim and the trap can
      // never take it back. Same shape as Backup, Warmup and ClearOverlay.
      const active = document.activeElement as HTMLElement | null;
      if (!active || active === panel || !panel.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleRedeem = useCallback(async () => {
    sound.click();
    if (busy || !code.trim()) return;
    setBusy(true);
    spanAtRedeem.current = paidSpan(missions);
    const result = await redeem(code);
    setBusy(false);
    setSaid(result);
    if (result.state !== 'redeemed') return;
    // Emptied on a code that landed, so the button now under the learner's
    // cursor cannot be pressed again for a confusing second answer.
    setCode('');
    onRedeemed();
  }, [busy, code, missions, redeem, onRedeemed]);

  const handleSignIn = useCallback(() => {
    sound.click();
    account.signIn();
  }, [account]);

  let outcome = '';
  let tone = '';
  if (said?.state === 'redeemed') {
    if (said.already) {
      outcome = s.said.already;
    } else {
      const span = spanAtRedeem.current;
      // A code that unlocked something we could not name is still a code that
      // worked, so there is a sentence for every reading of the catalogue.
      //
      // The old fallback passed `0` for both ends of the range when the span
      // could not be read, which announced that levels 0 to 0 had arrived —
      // a blank dressed up as two numbers. Each of the three readings now has
      // a sentence of its own: the rows, when the paid half is one lane's
      // tail and can be pointed at; how many there are, when it cannot; and,
      // when this browser saw nothing locked at all as the code landed, the
      // one thing that is certainly true — the whole course is on the map.
      outcome = span
        ? span.range
          ? s.said.redeemed(span.range.from, span.range.to)
          : s.said.redeemedCount(span.count)
        : s.said.redeemedAll;
    }
    tone = ' is-good';
  } else if (said) {
    outcome = s.said[said.state];
    // Malformed is the one refusal that is a correction — the string in the
    // box is wrong and looking at it again fixes that — so it is the one that
    // reads as one. The rest are flat, `spent` included: a used code is a
    // dead end however politely its sentence points at the next move, and
    // dressing it as a warning would suggest there is something to re-check.
    tone = said.state === 'malformed' ? ' is-warn' : ' is-bad';
  }

  // Asked for before the code is sent, not after it comes back 401. Somebody
  // holding a code should not have to spend it to discover they needed an
  // account first.
  const needsAccount = !account.user;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="backup-scrim"
          variants={scrimVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            className="backup invite"
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="invite-title"
            variants={panelVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <header className="backup-head">
              <span className="label">{s.label}</span>
              <h2 className="backup-title" id="invite-title">
                {s.title}
              </h2>
              <p className="backup-lede">{s.lede}</p>
            </header>

            <div className="backup-body">
              <section className="backup-half">
                <label className="label" htmlFor="invite-code">
                  {s.field}
                </label>
                <textarea
                  id="invite-code"
                  className="backup-field scroll"
                  ref={fieldRef}
                  value={code}
                  rows={2}
                  dir="ltr"
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                  placeholder={s.placeholder}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter submits; a code is one line however it wraps, and
                    // there is nothing here a newline would mean.
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void handleRedeem();
                    }
                  }}
                />
                <div className="backup-acts">
                  {needsAccount ? (
                    // Only when there is a sign-in to offer. A deployment with
                    // accounts switched off has no way to hold an entitlement
                    // at all, and a button that cannot work is worse than the
                    // sentence beside it, which is true either way.
                    account.enabled && (
                      <Button variant="solid" onClick={handleSignIn} disabled={account.busy}>
                        {account.busy ? signingIn : s.signIn}
                      </Button>
                    )
                  ) : (
                    <Button variant="solid" onClick={() => void handleRedeem()} disabled={busy || code.trim() === ''}>
                      {busy ? s.redeeming : s.redeem}
                    </Button>
                  )}
                  <span className="note">{needsAccount ? s.signedOut : s.privacy}</span>
                </div>
                <p className={`backup-out${tone}`} role="status" aria-live="polite">
                  {outcome}
                </p>
              </section>
            </div>

            <footer className="backup-foot">
              <span className="note" />
              <Button variant="quiet" onClick={onClose} title={s.closeWhy}>
                {s.close}
              </Button>
            </footer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
